import { createShopPayButton } from '@theme/gsg-shop-pay-sdk';
import { applyWelcomeGift, flyProductToCart, welcomeShopCheckoutUrl } from '@theme/gsg-cart-perks';
/**
 * <buy-box-tiers>
 *
 * Quantity-tiers buy box with per-unit variant selection.
 *
 * Adapts to the product automatically:
 *   - product has no real variants  -> no selectors are rendered at all
 *   - product has 1..n options      -> one dropdown per option, per unit
 *
 * The exact variant is resolved from the FULL option combination
 * (e.g. Color = White AND Size = Large), never from a single option value.
 */

/** Notify themes that use Shopify's pub/sub bus (Dawn-style) as well. */
import { CartLinesUpdateEvent, CartErrorEvent } from "@shopify/events";

/**
 * Best-effort notification for themes that rely on Shopify's legacy pub/sub bus.
 * @param {{ data: unknown }} detail
 */
function publishIfAvailable(detail) {
  try {
    const w = /** @type {any} */ (window);
    if (typeof w.publish === "function" && w.PUB_SUB_EVENTS) {
      w.publish(w.PUB_SUB_EVENTS.cartUpdate, detail.data);
    }
  } catch (e) {
    /* optional */
  }
}

class BuyBoxTiers extends HTMLElement {
  /** @type {HTMLElement[]} */
  tiers = [];
  /** @type {HTMLTemplateElement | null} */
  template = null;
  /** @type {HTMLButtonElement | null} */
  addBtn = null;
  /** @type {HTMLElement | null} */
  errorMsg = null;

  /** @type {boolean} */
  hasOptions = false;
  /** @type {number} */
  optionCount = 0;
  /** @type {number} */
  defaultVariantId = 0;
  /** @type {boolean} */
  redirectToCart = false;

  /** @type {{ soldOut: string; unavailable: string; adding: string; add: string }} */
  text = { soldOut: "", unavailable: "", adding: "", add: "" };

  /** @type {any[]} */
  variants = [];

  connectedCallback() {
    this.tiers = /** @type {HTMLElement[]} */ (
      Array.from(this.querySelectorAll("[data-tier]"))
    );
    this.template = /** @type {HTMLTemplateElement | null} */ (
      this.querySelector("template[data-option-group-template]")
    );
    this.addBtn = /** @type {HTMLButtonElement | null} */ (
      this.querySelector('[ref="addToCartBtn"]')
    );
    this.errorMsg = /** @type {HTMLElement | null} */ (
      this.querySelector('[ref="errorMsg"]')
    );

    this.hasOptions = this.dataset.hasOptions === "true";
    this.optionCount = parseInt(this.dataset.optionCount || "0", 10);
    this.defaultVariantId = parseInt(this.dataset.defaultVariantId || "0", 10);
    this.redirectToCart = this.dataset.redirectToCart === "true";

    this.text = {
      soldOut: this.dataset.soldOutText || "Sold out",
      unavailable:
        this.dataset.unavailableText || "This combination is unavailable",
      adding: this.dataset.addingText || "Adding...",
      add: this.dataset.addText || "Add to cart",
    };

    this.variants = this.readVariantData();

    this.tiers.forEach((tier) => {
      const input = /** @type {HTMLInputElement | null} */ (
        tier.querySelector('input[type="radio"]')
      );
      if (input) input.addEventListener("change", () => this.onTierChange());
    });

    this.pageController?.abort();
    this.pageController = new AbortController();
    window.addEventListener('pageshow', () => {
      this.checkoutBusy = false;
      this.querySelectorAll('[aria-busy]').forEach(button => button.removeAttribute('aria-busy'));
      this.refreshButtonState();
    }, {signal:this.pageController.signal});
    this.onTierChange();
    this.querySelector("[data-bundle-checkout]")?.addEventListener("click", (event) => this.checkout(event.currentTarget));

    if (this.addBtn) {
      this.addBtn.addEventListener("click", () => this.addToCart());
    }
  }

  readVariantData() {
    const node = this.querySelector("script[data-variant-data]");
    if (!node) return [];
    try {
      const parsed = JSON.parse(node.textContent ?? "");
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }

  /* ---------------------------------------------------------- tiers ---- */

  onTierChange() {
    const remembered = (this.lastSelections || this.getActiveGroups().map(group => this.getSelectedOptions(group)));
    const initial = this.variants.find(v => Number(v.id) === this.defaultVariantId)?.options;
    this.tiers.forEach((tier) => {
      const input = /** @type {HTMLInputElement | null} */ (
        tier.querySelector('input[type="radio"]')
      );
      const isChecked = !!input && input.checked;

      tier.classList.toggle("buy-box-tiers__tier--checked", isChecked);

      const list = /** @type {HTMLElement | null} */ (
        tier.querySelector("[data-option-list]")
      );
      if (!list) return; // product has no options: nothing to render

      if (!isChecked) {
        list.innerHTML = "";
        return;
      }

      const qty = Math.max(
        1,
        parseInt(input?.dataset.quantity ?? "1", 10) || 1,
      );
      list.innerHTML = "";

      if (!this.template) return;

      for (let i = 0; i < qty; i++) {
        const clone = this.template.content.cloneNode(true);
        list.appendChild(clone);
      }

      const groups = /** @type {HTMLElement[]} */ (
        Array.from(list.querySelectorAll("[data-option-group]"))
      );
      groups.forEach((group, unitIndex) => {
        const selects = /** @type {HTMLSelectElement[]} */ (
          Array.from(group.querySelectorAll("[data-option-select]"))
        );
        selects.forEach((select, optionIndex) => {
          const desired = (remembered[unitIndex] || remembered[0] || initial)?.[optionIndex];
          if (desired && [...select.options].some(option => option.value === desired)) select.value = desired;
          select.addEventListener("change", () => this.onOptionChange(group, true));
        });
        this.onOptionChange(group);
      });
    });

    this.refreshButtonState();
  }

  /* -------------------------------------------------------- variants ---- */

  /** @param {HTMLElement} group */
  getSelectedOptions(group) {
    const selects = /** @type {HTMLSelectElement[]} */ (
      Array.from(group.querySelectorAll("[data-option-select]"))
    );
    return selects.map((select) => select.value);
  }

  /** @param {string[]} options */
  findVariant(options) {
    return this.variants.find((variant) => {
      if (!Array.isArray(variant.options)) return false;
      if (variant.options.length !== options.length) return false;
      return variant.options.every(
        (/** @type {string} */ value, /** @type {number} */ index) =>
          value === options[index],
      );
    });
  }

  /**
   * Disable option values that cannot produce an available variant.
   * @param {HTMLElement} group
   */
  updateAvailability(group) {
    const selects = /** @type {HTMLSelectElement[]} */ (
      Array.from(group.querySelectorAll("[data-option-select]"))
    );

    selects.forEach((select, index) => {
      const preceding = selects.slice(0, index).map((s) => s.value);
      let hasUsableSelection = false;
      /** @type {string | null} */
      let firstUsable = null;

      Array.from(select.options).forEach((opt) => {
        const match = this.variants.find((variant) => {
          if (!variant.available) return false;
          if (!Array.isArray(variant.options)) return false;
          if (variant.options[index] !== opt.value) return false;
          return preceding.every((value, i) => variant.options[i] === value);
        });

        const usable = !!match && match.available;
        opt.disabled = !usable;

        const base = opt.dataset.baseLabel || (opt.textContent ?? "").trim();
        opt.dataset.baseLabel = base;
        opt.textContent = usable ? base : `${base} - ${this.text.soldOut}`;

        if (usable) {
          if (firstUsable === null) firstUsable = opt.value;
          if (opt.value === select.value) hasUsableSelection = true;
        }
      });

      if (!hasUsableSelection && firstUsable !== null) {
        select.value = firstUsable;
      }
    });
  }

  /** @param {HTMLElement} group */
  onOptionChange(group, refreshGallery = false) {
    this.updateAvailability(group);

    const options = this.getSelectedOptions(group);
    const variant = this.findVariant(options);
    const status = /** @type {HTMLElement | null} */ (
      group.querySelector("[data-unit-status]")
    );
    const img = /** @type {HTMLImageElement | null} */ (
      group.querySelector("[data-swatch-img]")
    );

    if (variant) {
      group.dataset.variantId = variant.id;
      group.dataset.available = variant.available ? "true" : "false";

      if (img && variant.image) img.src = variant.image;

      if (status) {
        if (variant.available) {
          status.hidden = true;
          status.textContent = "";
        } else {
          status.hidden = false;
          status.textContent = this.text.soldOut;
        }
      }
    } else {
      delete group.dataset.variantId;
      group.dataset.available = "false";

      if (status) {
        status.hidden = false;
        status.textContent = this.text.unavailable;
      }
    }

    if (refreshGallery && variant?.available) this.updateGallery(variant.id);
    this.lastSelections = this.getActiveGroups().map(group => this.getSelectedOptions(group));
    this.refreshButtonState();
  }

  /* ---------------------------------------------------------- button ---- */

  getActiveTier() {
    return this.tiers.find((tier) => {
      const input = /** @type {HTMLInputElement | null} */ (
        tier.querySelector('input[type="radio"]')
      );
      return !!input && input.checked;
    });
  }

  getActiveGroups() {
    const tier = this.getActiveTier();
    if (!tier) return [];
    return /** @type {HTMLElement[]} */ (
      Array.from(tier.querySelectorAll("[data-option-group]"))
    );
  }

  isSelectionValid() {
    if (this.dataset.available !== "true") return false;
    if (!this.hasOptions) return true;
    const groups = this.getActiveGroups();
    if (!groups.length) return false;
    return groups.every(
      (group) =>
        group.dataset.available === "true" && !!group.dataset.variantId,
    );
  }

  refreshButtonState() {
    this.updateTierPrices();
    this.dispatchEvent(new CustomEvent("gsg:bundle-change", { bubbles: true }));
    const valid = this.isSelectionValid();
    const checkout = this.querySelector('[data-bundle-checkout]');
    if (checkout) checkout.disabled = !valid;
    const selection = this.selection();
    const native = this.querySelector('[data-gsg-native-shop]');
    const mixed = this.querySelector('[data-gsg-mixed-shop]');
    const more = this.querySelector('[data-bundle-checkout]');
    const uniform = false;
    if (native) {
      native.hidden = !uniform;
      native.inert = !valid || this.addBtn?.dataset.busy === 'true';
      const values = {id:String(selection.items[0]?.id || this.defaultVariantId),quantity:String(selection.items[0]?.quantity || 1)};
      for (const [name,value] of Object.entries(values)) {
        const input = native.elements.namedItem(name);
        if (input.value !== value) { input.value = value; input.dispatchEvent(new Event('change',{bubbles:true})); }
      }
    }
    if (mixed) {
      mixed.hidden = uniform;
      if (!uniform) {
        mixed.replaceChildren(createShopPayButton({
          variants: selection.items.map(item=>({id:String(item.id),quantity:item.quantity})),
          disabled: !valid || this.addBtn?.dataset.busy === 'true',
          checkoutUrl: welcomeShopCheckoutUrl(this.dataset.productId),
          source: 'online_store',
          width: '100%',borderRadius:'12px'
        }));
      }
    }
    if (more) more.hidden = uniform;
    if (!this.addBtn || this.addBtn.dataset.busy === "true") return;
    this.addBtn.disabled = !valid;
    this.setAddLabel(valid ? this.text.add : this.text.soldOut);

  }

  setAddLabel(text) {
    const label = this.addBtn?.querySelector('[data-cart-button-label]');
    if (label) label.textContent = text;
  }

  async updateGallery(id) {
    this.dataset.galleryState = 'loading';
    this.galleryRequest?.abort();
    this.galleryRequest = new AbortController();
    const section = this.closest('[id^="shopify-section-"]');
    const gallery = section?.querySelector('media-gallery');
    if (!section || !gallery) return;
    const source = /^https?:/.test(window.location.href) ? window.location.href : this.dataset.productUrl || document.baseURI;
    const url = new URL(source,document.baseURI);
    url.searchParams.set('variant',String(id));
    url.searchParams.set('section_id',section.id.replace('shopify-section-',''));
    try {
      const response = await fetch(url.toString(),{signal:this.galleryRequest.signal});
      if (!response.ok) { this.dataset.galleryState = String(response.status); return; }
      const doc = new DOMParser().parseFromString(await response.text(),'text/html');
      const next = doc.querySelector('media-gallery');
      if (next) { gallery.replaceWith(next); this.dataset.galleryState = 'updated'; } else this.dataset.galleryState = 'missing-gallery';
    } catch (error) { this.dataset.galleryState = error.name; }
  }

  shopUrl(items) {
    const root = window.Shopify?.routes?.root || '/';
    return root + 'cart/' + items.map(item => `${item.id}:${item.quantity}`).join(',') + '?payment=shop_pay';
  }

  money(value) {
    return new Intl.NumberFormat(document.documentElement.lang || 'en', {style:'currency', currency:this.dataset.currency || 'USD'}).format(value / 100);
  }

  selection() {
    const items = this.buildItems() || [];
    const activeDiscount = Number(this.getActiveTier()?.querySelector('input[type="radio"]')?.dataset.discount || 0);
    let total = 0, compare = 0;
    for (const item of items) {
      const variant = this.variants.find(v => Number(v.id) === item.id);
      const price = variant?.price ?? Number(this.dataset.unitPrice);
      total += Math.round(price * item.quantity * (100 - activeDiscount) / 100);
      compare += Math.max(price, variant?.compare ?? Number(this.dataset.comparePrice)) * item.quantity;
    }
    return {items, total, compare, quantity:items.reduce((sum,item)=>sum+item.quantity,0), available:this.isSelectionValid(), id:items[0]?.id};
  }

  updateTierPrices() {
    const selected = this.selection();
    const first = this.variants.find(v => Number(v.id) === selected.id);
    const unitPrice = first?.price ?? Number(this.dataset.unitPrice);
    const unitCompare = Math.max(unitPrice, first?.compare ?? Number(this.dataset.comparePrice));
    const section = this.closest('[id^="shopify-section-"]');
    section?.querySelectorAll('product-price .price-item--sale, product-price .price__regular .price').forEach(node => { node.textContent = this.money(unitPrice); });
    section?.querySelectorAll('product-price .compare-at-price').forEach(node => { node.textContent = this.money(unitCompare); node.hidden = unitCompare <= unitPrice; });
    this.tiers.forEach(tier => {
      const tierDiscount = Number(tier.querySelector('input[type="radio"]')?.dataset.discount || 0);
      const tierUnitPrice = Math.round(unitPrice * (100 - tierDiscount) / 100);
      const now = tier.querySelector('.buy-box-tiers__price-now');
      if (now?.firstChild) now.firstChild.textContent = this.money(tierUnitPrice) + ' ';
      const was = tier.querySelector('.buy-box-tiers__price-was');
      if (was) { was.textContent = this.money(unitCompare); was.hidden = unitCompare <= tierUnitPrice; }
      const total = tier.querySelector('.buy-box-tiers__tier-total');
      if (total) total.textContent = this.money(tierUnitPrice * Number(tier.dataset.quantity));
    });
  }

  async checkout(button) {
    this.clearError();
    const selection = this.selection();
    if (!selection.available || !selection.items.length || this.checkoutBusy) return false;
    this.checkoutBusy = true;
    if (button) { button.disabled = true; button.setAttribute('aria-busy','true'); }
    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(root + 'cart/add.js', {method:'POST',headers:{'Content-Type':'application/json','Accept':'application/json'}, body:JSON.stringify({items:selection.items})});
      const result = await response.json();
      if (!response.ok || result.status) throw new Error(result.description || this.text.unavailable);
      const cart = await (await fetch(root + 'cart.js', {headers:{Accept:'application/json'}})).json();
      await applyWelcomeGift(cart);
      window.location.assign(root + 'checkout');
      return true;
    } catch (error) {
      this.showError(error.message || this.text.unavailable);
      this.checkoutBusy = false;
      if (button) { button.disabled = false; button.removeAttribute('aria-busy'); }
      return false;
    }
  }

  /* ------------------------------------------------------------ cart ---- */

  buildItems() {
    const tier = this.getActiveTier();
    if (!tier) return [];
    const input = /** @type {HTMLInputElement | null} */ (
      tier.querySelector('input[type="radio"]')
    );
    if (!input) return [];
    const qty = Math.max(1, parseInt(input.dataset.quantity ?? "1", 10) || 1);
    const tierLabel =
      tier.querySelector(".buy-box-tiers__tier-label")?.textContent?.trim() ||
      "Bundle";

    if (!this.hasOptions) {
      if (!this.defaultVariantId) return [];
      return [
        {
          id: this.defaultVariantId,
          quantity: qty,
          properties: { _Bundle: tierLabel },
        },
      ];
    }

    const counts = new Map();
    for (const group of this.getActiveGroups()) {
      const id = parseInt(group.dataset.variantId ?? "", 10);
      if (!id) return null;
      counts.set(id, (counts.get(id) || 0) + 1);
    }
    return Array.from(counts, ([id, quantity]) => ({
      id,
      quantity,
      properties: { _Bundle: tierLabel },
    }));
  }

  /** @param {string} message */
  showError(message) {
    if (!this.errorMsg) return;
    this.errorMsg.textContent = message;
    this.errorMsg.hidden = false;
  }

  clearError() {
    if (!this.errorMsg) return;
    this.errorMsg.hidden = true;
    this.errorMsg.textContent = "";
  }

  /**
   * Section ids Horizon needs re-rendered after a cart change.
   */
  getCartSectionIds() {
    const ids = new Set();
    document
      .querySelectorAll(
        "cart-drawer-component[data-section-id], cart-items-component[data-section-id], cart-icon-component[data-section-id], [id^='shopify-section-'] cart-drawer-component, [id^='shopify-section-'] cart-icon-component",
      )
      .forEach((el) => {
        const explicit =
          el instanceof HTMLElement ? el.dataset.sectionId : undefined;
        if (explicit) {
          ids.add(explicit);
          return;
        }
        const wrapper = el.closest("[id^='shopify-section-']");
        if (wrapper && wrapper.id) {
          ids.add(wrapper.id.replace("shopify-section-", ""));
        }
      });

    // Horizon's default cart sections.
    ids.add("cart-drawer");
    ids.add("cart-icon-bubble");

    return Array.from(ids);
  }

  /** Swap in freshly rendered section HTML returned by /cart/add.js. */
  /** @param {Record<string, string>} sections */
  renderSections(sections) {
    if (!sections) return;

    Object.entries(sections).forEach(([id, html]) => {
      if (typeof html !== "string") return;

      const doc = new DOMParser().parseFromString(html, "text/html");

      // Virtual sections (e.g. cart-icon-bubble) render as a bare element
      // with that id — no shopify-section- wrapper. Try that match first.
      const directTarget = document.getElementById(id);
      const directFresh = doc.getElementById(id);

      if (directTarget && directFresh) {
        directTarget.innerHTML = directFresh.innerHTML;
        return;
      }

      // Fall back to real, page-rendered sections.
      const target =
        document.getElementById(`shopify-section-${id}`) ||
        (document
          .querySelector(`[data-section-id="${id}"]`)
          ?.closest("[id^='shopify-section-']") ??
          null);

      if (!target) return;

      const fresh =
        doc.getElementById(`shopify-section-${id}`) ||
        doc.body.firstElementChild;
      if (fresh) {
        /** @type {HTMLElement} */ (target).innerHTML =
          /** @type {HTMLElement} */ (fresh).innerHTML;
      }
    });
  }

  openCartDrawer() {
    const themeDrawer = document.querySelector("theme-drawer#cart-drawer");
    if (themeDrawer && typeof themeDrawer.open === "function") {
      themeDrawer.open();
    }
  }

  async addToCart(source = this.addBtn) {
    this.clearError();
    if (!this.isSelectionValid()) { this.showError(this.text.unavailable); return; }

    const items = this.buildItems();
    if (!items || !items.length) {
      this.showError(this.text.unavailable);
      return;
    }

    if (!this.addBtn) return;
    this.addBtn.dataset.busy = "true";
    this.addBtn.disabled = true;
    this.setAddLabel(this.text.adding);
    this.refreshButtonState();

    const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);
    const deferred = CartLinesUpdateEvent.createPromise();

    // Dispatch BEFORE the fetch — cart-icon.js awaits event.promise, it
    // doesn't read detail values off this event directly.
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: "add",
        context: "product",
        lines: items.map((item) => ({
          merchandiseId: String(item.id),
          quantity: item.quantity,
        })),
        promise: deferred.promise,
      }),
    );

    try {
      const response = await fetch("/cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ items, sections: this.getCartSectionIds() }),
      });

      if (!response.ok) {
        let message = "Could not add to cart";
        try {
          const err = await response.json();
          message = err.description || err.message || message;
        } catch (e) {}
        throw new Error(message);
      }

      const result = await response.json().catch(() => ({}));

      let cart = null;
      try {
        cart = await (
          await fetch("/cart.js", { headers: { Accept: "application/json" } })
        ).json();
      } catch (e) {}

      const variant = this.variants.find(item => Number(item.id) === items[0].id);
      const [updated] = await Promise.all([
        applyWelcomeGift(cart, this.getCartSectionIds()),
        flyProductToCart(source, variant?.image)
      ]);
      cart = updated || cart;
      result.sections = cart?.sections || result.sections;
      this.renderSections(result.sections);

      // Resolve the promise cart-icon.js is waiting on.
      deferred.resolve({
        cart: cart
          ? CartLinesUpdateEvent.createCartFromAjaxResponse(cart)
          : null,
        detail: {
          source: "buy-box-tiers",
          itemCount: cart ? cart.item_count : itemCount,
          productId: result.id,
          sections: result.sections,
          cart,
        },
      });

      if (this.redirectToCart) window.location.href = (window.Shopify?.routes?.root || '/') + 'cart';
      else this.openCartDrawer();
    } catch (e) {
      this.dispatchEvent(
        new CartErrorEvent({ error: e.message, code: "SERVICE_UNAVAILABLE" }),
      );
      deferred.reject(e); // Unresolved promises make cart-icon.js hang forever
      this.showError(e.message);
    } finally {
      if (this.addBtn) {
        this.addBtn.dataset.busy = "false";
        this.setAddLabel(this.text.add);
      }
      this.refreshButtonState();
    }
  }
}

if (!customElements.get("buy-box-tiers")) {
  customElements.define("buy-box-tiers", BuyBoxTiers);
}

